import { VisitTemplate, VisitTemplatePrescriptionOnlyQuery } from '@dreryk/egabinet-visit-frontend-api';
import {
   MedicalServicePerformableByPractitionerSimple,
   MedicalServicePerformableByPractitionerSimpleQuery,
   ReceptionServiceForPractitionerSimple,
   ReceptionServiceForPractitionerSimpleQuery,
   ServicesTemplate,
   ServicesTemplateQuery,
} from '@dreryk/egabinet-private-medical-service-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { useCqrsSearch } from '@dreryk/command-query-react-common';
import { commonTranslationKeys } from '@dreryk/react-common';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { useGetCurrentPractitionerGlobalState } from '~libs.shared.account';

import { PRESCRIPTION_ONLY_TEMPLATE_TAG } from './PrescriptionVisitTemplates.constants';

export type PrescriptionVisitTemplatesLoaderData = {
   visitTemplate: VisitTemplate;
   servicesTemplate: ServicesTemplate | undefined;
   receptionServices: ReceptionServiceForPractitionerSimple[];
   medicalServices: MedicalServicePerformableByPractitionerSimple[];
};

type PrescriptionVisitTemplatesLoaderProps = {
   children: (renderProps: PrescriptionVisitTemplatesLoaderData) => JSX.Element;
};

export const PrescriptionVisitTemplatesLoader = (props: PrescriptionVisitTemplatesLoaderProps): JSX.Element => {
   const performingPrivateMedicalServicesAvailable = protectFeature('performingPrivateMedicalServices');
   const currentPractitioner = useGetCurrentPractitionerGlobalState();

   const {
      data: visitTemplates,
      loading: visitTemplatesLoading,
      error: visitTemplatesError,
   } = useCqrsSearch<VisitTemplate>(() => new VisitTemplatePrescriptionOnlyQuery());

   const { data: servicesTemplates, loading: servicesLoading, error: servicesError } = useCqrsSearch<ServicesTemplate>(
      () => {
         const query = new ServicesTemplateQuery();
         query.practitionerId = PractitionerId.of(currentPractitioner.id);
         query.templateTag = PRESCRIPTION_ONLY_TEMPLATE_TAG;
         return query;
      },
      undefined,
      { enabled: performingPrivateMedicalServicesAvailable }
   );

   const {
      data: receptionServices,
      loading: receptionLoading,
      error: receptionError,
   } = useCqrsSearch<ReceptionServiceForPractitionerSimple>(
      () => {
         const query = new ReceptionServiceForPractitionerSimpleQuery();
         query.practitionerId = currentPractitioner.id;
         return query;
      },
      undefined,
      { enabled: performingPrivateMedicalServicesAvailable }
   );

   const {
      data: medicalServices,
      loading: medicalLoading,
      error: medicalError,
   } = useCqrsSearch<MedicalServicePerformableByPractitionerSimple>(
      () => {
         const query = new MedicalServicePerformableByPractitionerSimpleQuery();
         query.practitionerId = currentPractitioner.id;
         return query;
      },
      undefined,
      { enabled: performingPrivateMedicalServicesAvailable }
   );

   if (visitTemplatesLoading || servicesLoading || receptionLoading || medicalLoading) {
      return <LoaderSpinner />;
   }

   if (visitTemplatesError || servicesError || receptionError || medicalError) {
      return (
         <Message
            testId={'prescriptionVisitTemplatesFetchError'}
            label={commonTranslationKeys.unexpectedErrorHasOccurred}
            type={MessageType.NEGATIVE}
            displayIndicator
         />
      );
   }

   return props.children({
      visitTemplate: visitTemplates[0],
      servicesTemplate: servicesTemplates?.[0],
      receptionServices: receptionServices ?? [],
      medicalServices: medicalServices ?? [],
   });
};
